import {
  AlertsManager,
  createAlertsManager,
  GlobalStyles,
  Dropdown,
  Button,
  Flex,
  Toggle,
} from '@bigcommerce/big-design'
import { ConfigProvider } from '@d5/hooks'
import { useRBAC } from '@d5/rbac'
import { D5Loading, Layout } from '@d5/ui'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AiOutlineMenuFold,
  AiOutlineMenuUnfold,
  AiOutlineLogout,
  AiOutlineUser,
} from 'react-icons/ai'
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom'
import styled from 'styled-components'

import logo from './assets/logo.png'
import bgImg from './assets/pageLeft.svg'
import { routes, pageTypes, RouteItem } from './routes'
import { usePermissions } from './services'
import {
  useAppSelector,
  useAppDispatch,
  setSelectedMenuItems,
  setPlatformType,
} from './store'
import { IAlertMessage } from './types/stores'

import { Language, d5AlertsManager } from '@/components'
import { PlatformType } from '@/constants'
import { usePlatformItems, useStores } from '@/hooks'
import { isShowPlatform } from '@/utils'
import '@d5/i18n'

export const StyledToggle = styled('div')`
  #toggle-platform {
    margin-bottom: 0;
  }
`

export const alertsManager: any = createAlertsManager()

export const Message = {
  success: (msg: string) => {
    const alertMessage: IAlertMessage = {
      header: 'Success',
      messages: [
        {
          text: msg,
        },
      ],
      type: 'success',
      onClose: () => null,
      autoDismiss: true,
    }
    alertsManager.add(alertMessage)
  },
  error: (msg: string) => {
    const alertMessage: IAlertMessage = {
      header: 'Failed',
      messages: [
        {
          text: msg,
        },
      ],
      type: 'error',
      onClose: () => null,
      autoDismiss: true,
    }
    alertsManager.add(alertMessage)
  },
  warning: (msg: string) => {
    const alertMessage: IAlertMessage = {
      header: 'Warning',
      messages: [
        {
          text: msg,
        },
      ],
      type: 'warning',
      onClose: () => null,
      autoDismiss: true,
    }
    alertsManager.add(alertMessage)
  },
}

interface AuthRouteProps {
  route: RouteItem
}

const AuthRoute = React.memo(({ route }: AuthRouteProps) => {
  const { user_id } = useAppSelector((state) => state.global)
  const location = useLocation()
  const routeKey: string = location.pathname.split('/')[1]
  const needAuth = useMemo(
    () => !pageTypes.noFrame.includes(routeKey),
    [routeKey, pageTypes],
  )
  const { permissions, setPermissions, hasAnyPermission } = useRBAC()

  const permissionAction = usePermissions(user_id)
  useEffect(() => {
    if (needAuth) {
      permissionAction?.fetchData?.()
    }
  }, [needAuth, user_id])

  const [setGetStores] = useStores()

  useEffect(() => {
    if (permissionAction?.data?.data?.permissions?.length) {
      setPermissions?.(permissionAction?.data?.data?.permissions)
      setGetStores(true)
    }
  }, [permissionAction?.data?.data?.permissions])

  if (!permissions?.length && needAuth) return <D5Loading isGlobal />

  if (!route?.permissions || hasAnyPermission?.(route?.permissions)) {
    return (
      <>
        {route.element}
        <AlertsManager manager={alertsManager} />
        <AlertsManager manager={d5AlertsManager} />
      </>
    )
  }
  return <div />
})

function App() {
  const {
    platformType,
    selectedMenuItems,
    token,
    userName,
    userLogoUrl,
    userLogoTitle,
    isPictureInsteadTitle,
    supportedPlatforms,
  } = useAppSelector((state) => state.global)

  const { t } = useTranslation()

  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { hasAnyPermission } = useRBAC()

  const platformItems = usePlatformItems(supportedPlatforms as PlatformType)

  const createMenuItems = (configRoutes: typeof routes) =>
    configRoutes
      .filter((route) => {
        if (!route.text) {
          return false
        }
        if (
          route?.permissions?.length &&
          !hasAnyPermission?.(route?.permissions)
        ) {
          return false
        }
        return true
      })
      .map((route) => {
        const menuItem: { [key: string]: any } = {
          key: route.id,
          text: route?.text ? t(route.text) : route.text,
          icon: route.icon,
          onClick: () => {
            navigate(route.path as string)
          },
        }
        return menuItem
      })

  const isPlatform = useMemo(() => isShowPlatform(pathname), [pathname])

  const menuItems = createMenuItems(routes)
  const routeKey: string = pathname.split('/')[1]
  const memoPageType = useMemo(
    () => (pageTypes.noFrame.includes(routeKey) ? 'noFrame' : 'frame'),
    [pageTypes, routeKey],
  )

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(setSelectedMenuItems(routeKey))
  }, [pathname])

  const updatePageMeta = ({ title, favicon }: any) => {
    if (title) {
      document.title = title
    }

    if (favicon || title) {
      const faviconElement = document.querySelector("link[rel='icon']")
      if (faviconElement) {
        faviconElement.setAttribute('href', favicon)
      } else {
        const newFavicon = document.createElement('link')
        newFavicon.rel = 'icon'
        newFavicon.href = favicon
        document.head.appendChild(newFavicon)
      }
    }
  }

  const setTab = (value: string) => {
    dispatch(setPlatformType(value))
  }

  useEffect(() => {
    updatePageMeta({
      title: userLogoTitle || '',
      favicon: userLogoUrl || '',
    })
  }, [userLogoUrl, userLogoTitle])

  return (
    <ConfigProvider
      config={{
        token,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
        httpStrategy: {
          401: (error: any) => {
            if (error.config?.method?.toLowerCase() !== 'get') {
              Message.warning(t('loyout_global_permission_denied'))
            }
          },
        },
      }}
    >
      <GlobalStyles />
      <Layout
        bgImg={bgImg}
        logoSrc={logo}
        brandTitle={t('loyout_app_brand_title')}
        userLogoUrl={userLogoUrl}
        userLogoTitle={userLogoTitle}
        isPictureInsteadTitle={!!isPictureInsteadTitle}
        headerExtra={
          <Flex
            flexDirection="row"
            style={{
              gap: '16px',
              alignItems: 'center',
            }}
          >
            {isPlatform && (
              <StyledToggle>
                <Toggle
                  id="toggle-platform"
                  items={platformItems}
                  onChange={setTab}
                  value={platformType}
                />
              </StyledToggle>
            )}
            {/* Switch language. Temporarily not online. Use for local testing */}
            <Language />
            <Dropdown
              items={[
                {
                  content: t('loyout_app_logout'),
                  icon: (
                    <span>
                      <AiOutlineLogout size={16} />
                    </span>
                  ),
                  type: 'link',
                  url: '/login',
                },
              ]}
              placement="bottom-end"
              toggle={
                <Button iconLeft={<AiOutlineUser size={16} />} variant="subtle">
                  {userName}
                </Button>
              }
            />
          </Flex>
        }
        menu={{
          items: menuItems,
          selectedKeys: selectedMenuItems,
        }}
        renderToggleIcon={(isMiniNav: boolean) =>
          isMiniNav ? (
            <AiOutlineMenuUnfold size={20} />
          ) : (
            <AiOutlineMenuFold size={20} />
          )
        }
        pageType={memoPageType}
        copyright={t('loyout_app_menu_copyright', {
          fullYear: new Date().getFullYear(),
        })}
      >
        <Routes>
          {routes.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              element={
                token || route.isPublic ? (
                  <AuthRoute route={route} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          ))}
        </Routes>
      </Layout>
    </ConfigProvider>
  )
}

export default App
